import styled from "@emotion/styled";
import { ArrowRight } from "@mui/icons-material";
import { Breadcrumbs, Checkbox, Stack, Typography } from "@mui/material";
import { LoadingScreen } from "components/LoadingScreen";
import { Button } from "components/UI";
import { useChurchStore } from "features/churches";
import { capitalize, startCase } from "lodash";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useRequisition } from "../api";
import { SchedulePaymentModal } from "../components";
import { PayRequests } from "../components/Drawer";
import { RequisitionRequestForm } from "../components/Forms/RequisitionRequestForm";
import { MakePaymentModal } from "../components/Modals/MakePaymentModal";
import { statusColors, statusStyleTypes } from "../constants/statuses";
import { useExpenseStore } from "../store";

const Title = styled.h6`
  margin: 0;
  font-size: 12px;
  font-weight: ${({ bold }) => (bold ? 600 : 400)};
  cursor: pointer;
`;

const Status = styled.div`
  border-radius: 2px;
  background: ${({ color }) => color};
  color: #ffffff;
  padding: 4px 8px;
  text-transform: capitalize;
  font-size: 10px;
`;

const StyledAlert = styled.div`
  padding: 16px;
  background-color: ${props => props.bg};
  display: flex;
  align-items: center;
  gap: 16px;
  border-radius: 20px;
  flex: 1;

  .startIcon {
    min-height: 48px;
    min-width: 48px;
    background-color: ${props => props.iconBg};
    display: grid;
    place-items: center;
    font-size: 24px;
    border-radius: 100%;
    color: #ffffff;
  }

  div {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  .endIcon {
    min-height: 24px;
    min-width: 24px;
    background-color: #ffffff;
    display: grid;
    place-items: center;
    border-radius: 100%;
    margin-left: auto;
    cursor: pointer;
  }

  h3 {
    margin: 0;
  }

  p {
    margin: 0;
  }
`;

export const RequisitionPayment = () => {
  const [requisition, setRequisition] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isSchedulePaymentOpen, setIsSchedulePaymentOpen] = useState(false);

  const navigate = useNavigate();
  const { id } = useParams();
  const selectedChurch = useChurchStore(state => state.selectedChurch);
  const openMakePaymentModal = useExpenseStore(
    state => state.openMakePaymentModal
  );

  const { data, isLoading } = useRequisition({
    churchId: selectedChurch?.id,
    id
  });

  useEffect(() => {
    if (data) {
      setRequisition(data.result);
    }
  }, [data]);

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <Stack spacing={3}>
      <Breadcrumbs
        separator={<ArrowRight fontSize="small" />}
        aria-label="breadcrumb"
      >
        <Title onClick={() => navigate("/finances/temp/expense")}>
          Expense
        </Title>
        <Title onClick={() => navigate("/finances/temp/expense/requisition")}>
          Requisition
        </Title>
        <Title bold>Request</Title>
      </Breadcrumbs>

      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{
          backgroundColor: "#FAFAFA",
          borderRadius: 1,
          p: 2
        }}
      >
        <Stack>
          <Typography variant="subtitle2">Payable Request</Typography>
          <Typography variant="caption">
            View all payable requests and scheduled payments
          </Typography>
        </Stack>
        <Button variant="contained" onClick={() => setDrawerOpen(true)}>
          View payable request
        </Button>
      </Stack>

      <Stack direction="row" justifyContent="space-between" spacing={4}>
        <Stack
          direction="row"
          justifyContent="space-between"
          sx={{
            backgroundColor: "#FAFAFA",
            borderRadius: 1,
            p: 2,
            width: "900px"
          }}
        >
          <Stack>
            <Typography variant="caption">Status</Typography>
            <Status color={statusColors[requisition?.requisition_status]}>
              {startCase(capitalize(requisition?.requisition_status))}
            </Status>
          </Stack>
          <Stack>
            <Typography variant="caption">Available Budget</Typography>
            <Typography fontWeight="bold">
              {Number(requisition?.amount).toLocaleString()} ETB
            </Typography>
          </Stack>
          <Stack>
            <Typography variant="caption">Current Approver</Typography>
            <Stack>
              {requisition?.approvers?.map(approver => (
                <Stack direction="row" alignItems="center" key={approver.id}>
                  <Typography fontWeight="bold">
                    {approver.approver.first_name} {approver.approver.last_name}
                  </Typography>
                  <Checkbox
                    checked={approver.pivot.status === "APPROVED"}
                    readOnly
                  />
                </Stack>
              ))}
            </Stack>
          </Stack>
        </Stack>
        {(requisition?.requisition_status === "DRAFTED" ||
          requisition?.requisition_status === "SCHEDULED") && (
          <StyledAlert
            bg={
              statusStyleTypes[
                requisition?.requisition_status?.toLowerCase()
              ]?.["backgroundColor"]
            }
            iconBg={
              statusStyleTypes[
                requisition?.requisition_status?.toLowerCase()
              ]?.["iconBackgroundColor"]
            }
          >
            <span className="startIcon">
              {
                statusStyleTypes[
                  requisition?.requisition_status?.toLowerCase()
                ]?.["icon"]
              }
            </span>
            <div>
              <h3>
                {
                  statusStyleTypes[
                    requisition?.requisition_status?.toLowerCase()
                  ]?.["title"]
                }
              </h3>
              <p>
                {
                  statusStyleTypes[
                    requisition?.requisition_status?.toLowerCase()
                  ]?.["message"]
                }
              </p>
            </div>
          </StyledAlert>
        )}
      </Stack>

      {requisition && (
        <RequisitionRequestForm
          isView={true}
          isEditMode={false}
          data={requisition}
        />
      )}

      <Stack direction="row" justifyContent="flex-start" spacing={2}>
        <Button onClick={openMakePaymentModal}>Make Payment</Button>
        {requisition?.requisition_status?.toLowerCase() !== "scheduled" && (
          <Button
            flavor="outlined"
            onClick={() => setIsSchedulePaymentOpen(true)}
          >
            Schedule Payment
          </Button>
        )}
        <Button flavor="text">Cancel</Button>
      </Stack>

      <PayRequests
        currentRequisitionId={id}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      />

      <MakePaymentModal data={requisition} />

      <SchedulePaymentModal
        open={isSchedulePaymentOpen}
        onClose={() => setIsSchedulePaymentOpen(false)}
        requisition={requisition}
      />
    </Stack>
  );
};
